import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CommaInput from './CommaInput';
import {
  Card,
  SectionTitle,
  SectionContent,
  FormRow,
  FormFieldGroup,
  FormFieldLabel,
  FormFieldDesc,
  InputUnit,
  NoteText,
  Divider,
  DeductionCard,
  DeductionRate,
  DeductionLabel,
  DeductionDesc,
  RealInput,
  AddRangeButton,
  AddRangeText,
  ToggleSwitch,
  ToggleFieldRow,
  CheckRow,
  CheckLabel,
  CheckboxCircle,
  HelpTooltip,
} from './SalarySettingStyled';

const DEDUCTION_OPTIONS = [
  { key: 'TAX_3_3', rate: '3.3%', label: '사업소득세' },
  { key: 'INSURANCE_4', rate: '4대보험', label: '근로소득세' },
  { key: 'CUSTOM', rate: '직접입력', label: '항목 커스텀' },
  { key: 'NONE', rate: '안함', label: '공제 없음' },
];

const DEDUCTION_DESCRIPTIONS = {
  TAX_3_3: '지급액의 3.3%를 원천징수합니다.(소득세 3% + 지방소득세 0.3%)',
  INSURANCE_4:
    '국민연금 4.5% · 건강보험 3.595% · 고용보험 0.9% · 장기요양보험 (건강보험료의 12.95%)을 공제합니다.',
  NONE: '공제하지 않습니다. 지급액이 그대로 실지급액이 됩니다.',
};

const SalarySettingBasic = ({ settings, onSettingsChange, employee }) => {
  const { t } = useTranslation();
  // 이 컴포넌트는 횟수제와 월급제가 함께 쓴다(시간제는 HourlyBasicSection).
  // 커미션 관련 안내는 횟수제에만 맞는다. 최저시급 보정(applyMinWage)은 시간제 전용이며
  // 월급제는 지원하지 않는다(2026-09-21, 사용자 결정 — 화면·백엔드 모두 제거).
  const isMonthly = settings.salaryType === 'MONTHLY';
  const customDeductions = settings.customDeductions || [];
  const hireDate = employee?.hireDate || employee?.hiredDate || '';

  const handleAddDeduction = () => {
    onSettingsChange('customDeductions', [
      ...customDeductions,
      { itemName: '', rate: 0, itemOrder: customDeductions.length },
    ]);
  };

  const handleRemoveDeduction = idx => {
    onSettingsChange(
      'customDeductions',
      customDeductions.filter((_, i) => i !== idx),
    );
  };

  const handleDeductionChange = (idx, field, value) => {
    const updated = customDeductions.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item,
    );
    onSettingsChange('customDeductions', updated);
  };

  return (
    <Card>
      <SectionTitle>{t('급여 기본 설정')}</SectionTitle>

      <SectionContent sx={{ mt: '24px' }}>
        {/* 적용 시작일 */}
        <FormRow>
          <FormFieldGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormFieldLabel>{t('적용 시작일')}</FormFieldLabel>
              <HelpTooltip title="이 날짜 이후의 수업만 정산에 포함됩니다. '입사일과 동일' 체크 시 해당 직원의 입사일이 자동 적용됩니다." />
            </Box>
            <FormFieldDesc>{t('설정한 급여의 적용 시작일')}</FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <RealInput
              type="date"
              inputWidth="200px"
              textAlign="left"
              value={settings.useHireDateAsStart === 'Y' ? (hireDate || '') : (settings.effectiveStartDate || '')}
              onChange={e => onSettingsChange('effectiveStartDate', e.target.value || null)}
              disabled={settings.useHireDateAsStart === 'Y'}
              style={settings.useHireDateAsStart === 'Y' ? { backgroundColor: '#F0F0F0' } : {}}
            />
            <CheckRow
              onClick={() => {
                const next = settings.useHireDateAsStart === 'Y' ? 'N' : 'Y';
                onSettingsChange('useHireDateAsStart', next);
                if (next === 'Y') onSettingsChange('effectiveStartDate', null);
              }}
              sx={{ cursor: 'pointer' }}
            >
              <CheckboxCircle isChecked={settings.useHireDateAsStart === 'Y'}>
                {settings.useHireDateAsStart === 'Y' && <CheckIcon sx={{ fontSize: 16 }} />}
              </CheckboxCircle>
              <CheckLabel>{t('입사일과 동일')}</CheckLabel>
            </CheckRow>
          </Box>
        </FormRow>

        <Divider />

        {/* 기본 급여액 */}
        <FormRow>
          <FormFieldGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormFieldLabel required>{t('기본 급여액')}</FormFieldLabel>
              <HelpTooltip title={isMonthly
                ? "매월 고정으로 지급되는 급여입니다. 성과급·근로수당·비과세 항목과 별도로 합산됩니다."
                : "매월 고정으로 지급되는 기본 급여입니다. 수업 수당, 성과급 등과 별도로 합산됩니다. 0원 입력 시 수업 수당만 지급됩니다."} />
            </Box>
            <FormFieldDesc>
              {isMonthly
                ? t('매월 고정으로 지급되는 급여')
                : t('수업 커미션 외 기본으로 지급되는 고정 금액')}
            </FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <CommaInput
              inputWidth="200px"
              value={settings.baseAmount}
              onChange={val => onSettingsChange('baseAmount', val)}
            />
            <InputUnit>{t('원')}</InputUnit>
          </Box>
          {/* 커미션 안내는 횟수제에만 해당한다 — 월급제는 수업 수당 섹션 자체가 렌더링되지 않아
              (SalarySetting.jsx 의 salaryType === 'COUNT' 조건) 0원을 넣어도 받을 커미션이 없다. */}
          {!isMonthly && <NoteText>{t('※ 0원 입력 시 커미션만 지급')}</NoteText>}
        </FormRow>

        <Divider />

        {/* 공제 항목 */}
        <FormRow sx={{ alignItems: 'flex-start' }}>
          <FormFieldGroup>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FormFieldLabel required>{t('공제 항목')}</FormFieldLabel>
              <HelpTooltip title={"3.3%: 사업소득세 (소득세 3% + 지방소득세 0.3%)\n4대보험: 국민연금·건강보험·장기요양·고용보험\n직접입력: 원하는 공제 항목과 비율을 직접 설정\n안함: 공제하지 않음\n\n비과세 항목을 제외한 금액에 적용됩니다."} />
            </Box>
            <FormFieldDesc>{t('해당 직원에게 적용할 공제 방식을 선택 및 설정')}</FormFieldDesc>
          </FormFieldGroup>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              {DEDUCTION_OPTIONS.map(opt => (
                <DeductionCard
                  key={opt.key}
                  isSelected={settings.deductionType === opt.key}
                  onClick={() => onSettingsChange('deductionType', opt.key)}
                >
                  <DeductionRate isSelected={settings.deductionType === opt.key}>
                    {opt.rate}
                  </DeductionRate>
                  <DeductionLabel isSelected={settings.deductionType === opt.key}>
                    {opt.label}
                  </DeductionLabel>
                </DeductionCard>
              ))}
            </Box>

            {settings.deductionType === 'CUSTOM' ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {customDeductions.map((item, idx) => (
                  <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RealInput
                      inputWidth="200px"
                      textAlign="left"
                      placeholder={t('공제 항목명 (예 : 국민연금)')}
                      value={item.itemName}
                      onChange={e => handleDeductionChange(idx, 'itemName', e.target.value)}
                     
                    />
                    <CommaInput
                      inputWidth="80px"
                      placeholder="0.0"
                      value={item.rate}
                      onChange={val => handleDeductionChange(idx, 'rate', val)}
                    />
                    <InputUnit>%</InputUnit>
                    <DeleteOutlineIcon
                      sx={{ fontSize: 20, color: '#A6A6A6', cursor: 'pointer', '&:hover': { color: '#e53935' } }}
                      onClick={() => handleRemoveDeduction(idx)}
                    />
                  </Box>
                ))}
                <AddRangeButton onClick={handleAddDeduction}>
                  <AddCircleOutlineIcon sx={{ fontSize: 24, color: '#4AB3BC' }} />
                  <AddRangeText>{t('항목 추가')}</AddRangeText>
                </AddRangeButton>
              </Box>
            ) : (
              <DeductionDesc>{DEDUCTION_DESCRIPTIONS[settings.deductionType]}</DeductionDesc>
            )}
          </Box>
        </FormRow>
      </SectionContent>
    </Card>
  );
};

export default SalarySettingBasic;
